import React, { useEffect, useState } from "react";
import { collection, getDocs, query, orderBy, doc, getDoc } from "firebase/firestore";
import { db } from "../firebase/config";

const defaults = {
  bannerImage: "/authorcorner/banner.jpg",
  introHeading: "Rosalita Spreads Kindness",
  introSubheading: "About the Author",
  introParagraph1: "Kristin Schmidt Larson is the author of Traveling Caravan: Growing Up Under the Big Top. Her story reflects family, resilience, and a lifelong commitment to kindness and advocacy. This page introduces her book, her mission, and the places where readers can find it.",
  introParagraph2: "The book is available for purchase through major retailers and select bookstores. It is also offered in e-book format in several countries. Below you will find links, store listings, and availability information.",
  bookListHeading: "Rosalita Spreads Kindness:",
  bookListItems: ["Message for kids & children", "The newest beginning", "Travel stories, memories, and growth", "Words to live by", "D.B. Books"],
  comingSoonText: "/ Coming Soon to Bookstores in South Dakota.",
  authorImage: "/authorcorner/author-book.jpg",
  platformHeading: "My children\u2019s book is now available for purchase on these platforms",
  platformImages: ["/authorcorner/platform-1.jpg", "/authorcorner/platform-2.jpg", "/authorcorner/platform-3.jpg", "/authorcorner/platform-4.jpg"],
  purchaseHeading: "Traveling Caravan",
  purchaseParagraph: "A memoir of a life lived under the big top, Traveling Caravan: Growing Up Under the Big Top shares the author\u2019s journey, family memories, and lessons learned from a unique childhood.",
  kindnessHeading: "Rosalita Spreads Kindness",
  kindnessParagraph: "The message behind the book is simple: kindness matters, stories matter, and sharing our experiences helps others feel seen and supported.",
  availableHeading: "Available For Purchase",
  logoImages: ["/authorcorner/amazon-logo.png", "/authorcorner/barnes-logo.png"],
  storeImages: ["/upload/g1.png", "/upload/g2.png", "/upload/g3.png", "/upload/g4.png", "/upload/g5.png", "/upload/g6.png"],
  bookStoreHeading: "Bookstores",
  bookStoreDescription: "We\u2019re thrilled to announce that the first copy of our book is now available at select local stores!",
  ebookHeading: "E-book",
  ebookDescription: "Browse our collection of e-books available online.",
  subscribeHeading: "Get The Latest Updates",
  subscribePlaceholder: "Enter your email",
  subscribeButtonText: "Subscribe",
};

function Authorcorner() {
  const [content, setContent] = useState(defaults);
  const [bookStores, setBookStores] = useState([]);
  const [eBooks, setEBooks] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const snap = await getDoc(doc(db, "settings", "authorCornerContent"));
        if (snap.exists()) {
          const data = snap.data();
          setContent((prev) => ({
            ...prev, ...data,
            logoImages: data.logoImages?.length ? data.logoImages : prev.logoImages,
            storeImages: data.storeImages?.length ? data.storeImages : prev.storeImages,
          }));
        }
      } catch (e) {}
      try {
        const bs = await getDocs(query(collection(db, "bookStore"), orderBy("createdAt", "desc")));
        setBookStores(bs.docs.map((d) => d.data()));
      } catch (e) {}
      try {
        const eb = await getDocs(query(collection(db, "eBooks"), orderBy("createdAt", "desc")));
        setEBooks(eb.docs.map((d) => d.data()));
      } catch (e) {}
    };
    fetchData();
  }, []);

  const c = content;

  return (
    <div className="bg-white text-black" style={{ fontFamily: "Montserrat, sans-serif" }}>
      {/* TOP BANNER */}
      <section className="w-full">
        <img
          src={c.bannerImage}
          alt="Traveling Caravan banner"
          className="w-full h-auto object-cover"
        />
      </section>

      {/* INTRO SECTION */}
      <section className="max-w-[1180px] mx-auto px-4 md:px-8 pt-10 md:pt-14 pb-8">
        <div className="grid lg:grid-cols-[1.1fr_0.9fr] gap-10 items-start">
          {/* LEFT CONTENT */}
          <div>
            <h2 className="text-[18px] md:text-[22px] font-extrabold uppercase tracking-wide leading-tight">
              {c.introHeading}
            </h2>

            <h3 className="mt-2 text-[12px] md:text-[13px] font-extrabold uppercase tracking-wide">
              {c.introSubheading}
            </h3>

            <p className="mt-5 text-[11px] md:text-[12px] text-[#4f4f4f] leading-[1.9]">
              {c.introParagraph1}
            </p>

            <p className="mt-4 text-[11px] md:text-[12px] text-[#4f4f4f] leading-[1.9]">
              {c.introParagraph2}
            </p>

            <div className="mt-6 text-[11px] md:text-[12px] text-[#4f4f4f] leading-[1.9]">
              <p className="font-semibold text-black uppercase mb-2">{c.bookListHeading}</p>
              <ul className="space-y-2">
                {(c.bookListItems || []).map((item, i) => (
                  <li key={i}>• {item}</li>
                ))}
              </ul>
            </div>

            <p className="mt-6 text-[11px] md:text-[12px] font-semibold uppercase">
              {c.comingSoonText}
            </p>
          </div>

          {/* RIGHT IMAGE */}
          <div className="flex justify-center lg:justify-end">
            <img
              src={c.authorImage}
              alt="Author book cover"
              className="w-full max-w-[430px] object-contain"
            />
          </div>
        </div>
      </section>

      {/* BOOK IMAGES ROW */}
      <section className="max-w-[1180px] mx-auto px-4 md:px-8 py-6">
        <h3 className="text-center text-[11px] md:text-[12px] font-extrabold uppercase tracking-wide">
          {c.platformHeading}
        </h3>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
          {(c.platformImages || []).map((src, i) => (
            <img key={i} src={src} alt="" className="w-full h-[82px] md:h-[95px] object-cover" />
          ))}
        </div>
      </section>

      {/* TEXT + PURCHASE SECTION */}
      <section className="max-w-[1180px] mx-auto px-4 md:px-8 py-8 md:py-10">
        <div className="border-t border-gray-200 pt-8">
          <h3 className="text-[13px] md:text-[15px] font-extrabold uppercase tracking-wide">
            {c.purchaseHeading}
          </h3>

          <p className="mt-3 text-[11px] md:text-[12px] text-[#4f4f4f] leading-[1.9]">
            {c.purchaseParagraph}
          </p>

          <h4 className="mt-6 text-[13px] md:text-[15px] font-extrabold uppercase tracking-wide">
            {c.kindnessHeading}
          </h4>

          <p className="mt-3 text-[11px] md:text-[12px] text-[#4f4f4f] leading-[1.9]">
            {c.kindnessParagraph}
          </p>
        </div>
      </section>

      {/* AVAILABLE FOR PURCHASE */}
      <section className="max-w-[1180px] mx-auto px-4 md:px-8 py-6 md:py-8">
        <h2 className="text-center text-[24px] md:text-[42px] font-extrabold uppercase tracking-wide leading-none">
          Available For Purchase
        </h2>

        <div className="flex flex-col md:flex-row items-center justify-center gap-8 md:gap-20 mt-8 md:mt-10">
          <img src="/upload/author1.png" alt="" className="object-contain" />
          <img src="/upload/author2.png" alt="" className="object-contain" />
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-4">
          <img src="/upload/g1.png" alt="" className=" object-cover" />
          <img src="/upload/g2.png" alt="" className=" object-cover" />
          <img src="/upload/g3.png" alt="" className=" object-cover" />
          
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-4">
          <img src="/upload/g4.png" alt="" className=" object-cover" />
          <img src="/upload/g5.png" alt="" className=" object-cover" />
          <img src="/upload/g6.png" alt="" className=" object-cover" />
        </div>
      </section>

      {/* BOOKSTORES */}
      <section className="max-w-[1180px] mx-auto px-4 md:px-8 py-10 md:py-12">
        <h3 className="text-center text-[14px] md:text-[16px] font-extrabold uppercase tracking-wide">
          {c.bookStoreHeading}
        </h3>
        <p className="text-center mt-2 text-[11px] md:text-[12px] text-[#666]">
          {c.bookStoreDescription}
        </p>

        <div className="mt-5 flex justify-center">
          <div className="flex w-full max-w-[460px] border border-gray-200 rounded-full overflow-hidden bg-white">
            <input
              type="text"
              placeholder="Enter your town/city"
              className="flex-1 px-5 py-3 text-[11px] outline-none"
            />
            <button className="px-5 bg-black text-white text-[11px] font-semibold">
              Find
            </button>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-8 mt-10">
          {bookStores.map((store, idx) => (
            <div key={idx} className="flex gap-4">
              {store.image ? (
                <img src={store.image} alt="" className="w-20 h-20 object-cover rounded flex-shrink-0" />
              ) : (
                <div className="w-20 h-20 bg-gray-100 rounded flex-shrink-0" />
              )}
              <div className="text-[11px] md:text-[12px] leading-[1.7]">
                <p className="font-bold uppercase text-[13px]">{store.name}</p>
                {store.address && <p className="text-gray-600">{store.address}</p>}
                {store.phone && <p className="text-gray-600">{store.phone}</p>}
                {store.url && <a href={store.url} target="_blank" rel="noopener noreferrer" className="text-[#e84fa5] hover:underline text-[11px]">{store.url}</a>}
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-6">
          <button className="text-[11px] font-semibold uppercase text-[#444]">
            Show more
          </button>
        </div>
      </section>

      {/* E-BOOK */}
      <section className="max-w-[1180px] mx-auto px-4 md:px-8 py-10 md:py-12 border-t border-gray-200">
        <h3 className="text-center text-[14px] md:text-[16px] font-extrabold uppercase tracking-wide">
          {c.ebookHeading}
        </h3>
        <p className="text-center mt-2 text-[11px] md:text-[12px] text-[#666]">
          {c.ebookDescription}
        </p>

        <div className="grid md:grid-cols-3 gap-10 mt-10">
          {eBooks.map((book, idx) => (
            <div key={idx} className="text-center">
              {book.image ? (
                <a href={book.link || "#"} target="_blank" rel="noopener noreferrer">
                  <img src={book.image} alt={book.name} className="w-full max-w-[200px] h-52 object-cover rounded mx-auto shadow-md hover:shadow-lg transition-shadow cursor-pointer" />
                </a>
              ) : (
                <div className="w-full max-w-[200px] h-52 bg-gray-100 rounded mx-auto" />
              )}
              {book.name && <p className="mt-3 text-[14px] font-bold uppercase">{book.name}</p>}
            </div>
          ))}
        </div>
      </section>

      {/* SUBSCRIBE BAR */}
      <section className="bg-[#f4f4f4] border-t border-gray-200">
        <div className="max-w-[1180px] mx-auto px-4 md:px-8 py-6 md:py-8">
          <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-6">
            <h3 className="text-[11px] md:text-[13px] font-extrabold tracking-wide uppercase">
              {c.subscribeHeading}
            </h3>

            <div className="flex w-full md:w-auto">
              <input
                type="email"
                placeholder={c.subscribePlaceholder}
                className="h-9 md:h-10 w-full md:w-[220px] border border-gray-300 bg-white px-3 text-[11px] outline-none"
              />
              <button className="h-9 md:h-10 px-5 bg-black text-white text-[10px] md:text-[11px] font-semibold uppercase">
                {c.subscribeButtonText}
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Authorcorner;
